import Phaser from 'phaser';
import { getDialogueById } from '../data/dialogues';
import { InteractionContext, Interactable } from '../interfaces/Interactable';
import { SceneTransition } from '../utils/SceneTransition';
export type InteractionType = 'dialogue' | 'quest' | 'scene' | 'custom';

export interface InteractionZoneConfig {
  id: string;
  type: InteractionType;
  label?: string;
  dialogueId?: string;
  questId?: string;
  targetScene?: string;
  targetZone?: number;
  interactionRadius?: number;
  oneTime?: boolean;
  onInteract?: (ctx: InteractionContext) => void;
}

export class InteractionZone implements Interactable {
  readonly interactId: string;
  private x: number;
  private y: number;
  private config: InteractionZoneConfig;
  private interactionRadius: number;
  private triggered = false;
  private enabled = true;
  private zone?: Phaser.GameObjects.Zone;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    config: InteractionZoneConfig,
  ) {
    this.x = x;
    this.y = y;
    this.config = config;
    this.interactId = config.id;
    this.interactionRadius = config.interactionRadius ?? Math.max(width, height) * 0.6;

    this.zone = scene.add.zone(x, y, width, height);
    this.zone.setDepth(1);
  }

  getInteractPosition(): Phaser.Math.Vector2 {
    return new Phaser.Math.Vector2(this.x, this.y);
  }

  getInteractionRadius(): number {
    return this.interactionRadius;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  canInteract(): boolean {
    if (!this.enabled) return false;
    if (this.config.oneTime && this.triggered) return false;
    return true;
  }

  getPromptText(): string {
    return this.config.label ? `${this.config.label} — E` : 'Tekan E / Tap Aksi';
  }

  onInteract(ctx: InteractionContext): void {
    if (!this.canInteract()) return;

    if (this.config.onInteract) {
      this.config.onInteract(ctx);
      this.markTriggered();
      return;
    }

    switch (this.config.type) {
      case 'dialogue':
        if (this.config.dialogueId) {
          const dialogue = getDialogueById(this.config.dialogueId);
          if (dialogue) {
            ctx.dialogue.show(dialogue, () => {
              if (this.config.questId) {
                ctx.quest.completeQuest(this.config.questId!);
              }
              this.markTriggered();
            });
          }
        }
        break;
      case 'quest':
        if (this.config.questId) {
          ctx.quest.completeQuest(this.config.questId);
          this.markTriggered();
        }
        break;
      case 'scene':
        if (this.config.targetScene) {
          SceneTransition.startNextFrame(ctx.scene, this.config.targetScene);
        }
        break;
      case 'custom':
        break;
    }
  }

  private markTriggered(): void {
    if (this.config.oneTime) this.triggered = true;
  }

  setOnInteract(callback: (ctx: InteractionContext) => void): void {
    this.config.onInteract = callback;
  }

  destroy(): void {
    this.zone?.destroy();
  }
}
